import React, { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { FILTER_SEPARATOR, FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import { ApplicationApi, CustomFieldSearchSelectApi } from "../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../services/Api/api";

interface Props {
	form: UseFormReturn<ApplicationApi>;
	config: Record<string, any>;
}

interface CustomFieldDefaultValue extends CustomFieldSearchSelectApi {
	name: string;
}

const MultilocationCustomFieldsForm = ({ form, config }: Props) => {
	const { t } = useTranslation();
	const {
		formState: { errors },
		setValue,
		watch,
		control,
	} = form;
	const [defaultValues, setDefaultValues] = useState<CustomFieldDefaultValue[]>([]);
	const watchedFields = watch("settings.custom_fields");
	const watchedFieldsAsString = JSON.stringify(watchedFields);

	useEffect(() => {
		const lastValue = watchedFields[watchedFields.length - 1];
		if (lastValue) {
			setValue("settings.custom_fields", [...watchedFields, ""]);
		}
	}, [watchedFieldsAsString]);

	const getFieldDefaultValue = (slug: string) => {
		const field = [...config?.custom_fields, ...defaultValues].find(
			(customField: Record<string, any>) => customField.slug === slug
		);
		if (!field) return undefined;
		return {
			slug: field?.slug,
			label: field?.name,
		};
	};

	const searchCustomFields = async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		params = {
			...params,
			"resource_type|e": `SALES_ORDER${FILTER_VALUE_SEPARATOR}PURCHASE_ORDER`,
		};
		return api.organization().getCustomFieldsSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const handleRemoveCustomField = (index: number) => {
		const customFields = [...watch("settings.custom_fields")];
		const customFieldToRemove = customFields[index];
		if (customFieldToRemove) {
			customFields.splice(index, 1);
			setValue("settings.custom_fields", [...customFields]);
		}
	};

	const getCustomFieldSelectCustomActions = (index: number) => [
		<RemoveSVG
			key={`custom-field-${index}-remove-button`}
			className="preview-option"
			onMouseDown={(e) => {
				e.preventDefault();
				e.stopPropagation();
			}}
			onClick={() => handleRemoveCustomField(index)}
		/>,
	];

	const handleChangeOption = (option: CustomFieldSearchSelectApi) => {
		if (defaultValues.find((value) => value.slug === option.slug)) return;
		setDefaultValues([
			...defaultValues,
			{
				...option,
				name: option.label,
			},
		]);
	};

	return (
		<div className="mt-3">
			<h5>{t("modules.app.field.synchronize_custom_fields.title")}</h5>
			<div className="row">
				{(watchedFields || []).map((slug: string, index: number) => (
					<div className="col-md-4" key={`custom-field-${slug}-${index}`}>
						<FormSelectGroup
							errors={errors}
							control={control}
							isClearable={false}
							loadOptions={searchCustomFields}
							name={`settings.custom_fields.${index}`}
							onChange={(_, fullObj) => handleChangeOption(fullObj)}
							getOptionValue={(opt) => opt.slug}
							defaultValue={getFieldDefaultValue(slug)}
							customActions={
								index === watchedFields.length - 1 ? [] : getCustomFieldSelectCustomActions(index)
							}
							data-testid={`settings.custom_fields.${index}`}
						/>
					</div>
				))}
			</div>
		</div>
	);
};

export default MultilocationCustomFieldsForm;
